// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { InstrumentTypes } from '@shared/utils/Instruments/InstrumentTypes';
import { MarginInfoGroupType, MarginInfoItemType } from './MarginInfoEnums';
class _MarginInfoUtils {
    constructor() {
        this.TICKCOST_INS_TYPES = [
            InstrumentTypes.FUTURES,
            InstrumentTypes.CFD_FUTURES,
            InstrumentTypes.OPTIONS,
            InstrumentTypes.SPREADBET
        ];
        this.ALLOW_SHOW_POSITIONS = Resources.getResource('InstrumentDetailsPanel.Allow short positions');
        this.NOT_ALLOW_SHORT_POSITIONS = Resources.getResource('InstrumentDetailsPanel.Not allow short positions');
    }
    getGroupText(type, currency) {
        switch (type) {
            case MarginInfoGroupType.KEY_GROUP_RISKS:
                return `${Resources.getResource('OrderEntry.InfoBlock.1.Risks')} ${currency}`;
            case MarginInfoGroupType.KEY_GROUP_FEES:
                return `${Resources.getResource('OrderEntry.InfoBlock.2.Fees')} ${currency}`;
            default:
                return '';
        }
    }
    getItemKey(itemType) {
        switch (itemType) {
            // 1.Risks
            case MarginInfoItemType.KEY_BALANCE:
                return 'OrderEntry.InfoBlock.Balance';
            case MarginInfoItemType.KEY_AVAILABLE_FUNDS:
                return 'OrderEntry.InfoBlock.Availible funds';
            case MarginInfoItemType.KEY_INCOMING_FUNDS:
                return 'OrderEntry.InfoBlock.Incoming funds';
            case MarginInfoItemType.KEY_MARGIN_AVAILABLE:
                return 'OrderEntry.InfoBlock.Margin availible';
            case MarginInfoItemType.KEY_IMPACT_ON_FUNDS:
                return 'OrderEntry.InfoBlock.Impact on portfolio';
            case MarginInfoItemType.KEY_IMPACT_ON_MARGIN:
                return 'OrderEntry.InfoBlock.Impact on margin';
            case MarginInfoItemType.KEY_AFTER_TRADE_FUNDS:
                return 'OrderEntry.InfoBlock.After trade funds';
            case MarginInfoItemType.KEY_AFTER_TRADE_MARGIN:
                return 'OrderEntry.InfoBlock.After trade margin';
            case MarginInfoItemType.KEY_BLOCKED_FOR_STOCKS:
                return 'OrderEntry.InfoBlock.Blocked for Stocks';
            case MarginInfoItemType.KEY_SPREAD_INIT_LOSS:
                return 'OrderEntry.InfoBlock.Spread in loss';
            case MarginInfoItemType.KEY_PL_PER_TICK:
                return 'OrderEntry.InfoBlock.P/L per Tick';
            case MarginInfoItemType.KEY_ALLOW_SHORT_POSITIONS:
                return 'OrderEntry.InfoBlock.Allow short positions';
            case MarginInfoItemType.KEY_EST_AVAILABLE_CASH:
                return 'OrderEntry.InfoBlock.After trade cash';
            case MarginInfoItemType.BLOCKED_FOR_FIXED_INCOME:
                return 'OrderEntry.InfoBlock.Blocked for fixed income';
            // 2.Fees
            case MarginInfoItemType.KEY_FILL_PER_LOT:
                return 'OrderEntry.InfoBlock.FillPerLot';
            case MarginInfoItemType.KEY_ORDER_PER_LOT:
                return 'OrderEntry.InfoBlock.OrderPerLot';
            case MarginInfoItemType.KEY_ORDER_PER_LOT_WITH_FIXED_PART:
                return 'OrderEntry.InfoBlock.OrderPerLotWithFixedPart';
            case MarginInfoItemType.KEY_FILL_VOLUME:
                return 'OrderEntry.InfoBlock.FillVolume';
            case MarginInfoItemType.KEY_FILL_VOLUME_WITH_MIN_PD:
                return 'OrderEntry.InfoBlock.FillVolumeWithMinPD';
            case MarginInfoItemType.KEY_PER_FILL:
                return 'OrderEntry.InfoBlock.PerFill';
            case MarginInfoItemType.KEY_PER_TRANSACTION:
                return 'OrderEntry.InfoBlock.PerTransaction';
            case MarginInfoItemType.KEY_PER_PHONE_TRANSACTION:
                return 'OrderEntry.InfoBlock.PerPhoneTransaction';
            case MarginInfoItemType.KEY_VAT:
                return 'OrderEntry.InfoBlock.VAT';
            case MarginInfoItemType.KEY_ORDER_VOLUME:
                return 'OrderEntry.InfoBlock.OrderVolume';
            case MarginInfoItemType.KEY_SHORT_SWAP_INFO:
                return 'OrderEntry.InfoBlock.SwapDetails';
            case MarginInfoItemType.KEY_FREE_COMMISSION:
                return 'OrderEntry.InfoBlock.Free commission';
            case MarginInfoItemType.KEY_TOTAL_FEES:
                return 'OrderEntry.InfoBlock.TotalFee';
            case MarginInfoItemType.KEY_LONG_SWAP:
                return 'OrderEntry.InfoBlock.LongSwap';
            case MarginInfoItemType.KEY_SHORT_SWAP:
                return 'OrderEntry.InfoBlock.ShortSwap';
            case MarginInfoItemType.KEY_ORDER_CARE:
                return 'OrderEntry.InfoBlock.CareOrder';
            case MarginInfoItemType.KEY_FILL_FACE_VALUE:
                return 'OrderEntry.InfoBlock.FillFaceValue';
            default:
                return '';
        }
    }
    isItemHidden(itemType) {
        const key = this.getItemKey(itemType);
        if (!key)
            return true;
        return Resources.isHidden(key);
    }
    getItemText(itemType) {
        const key = this.getItemKey(itemType);
        if (!key)
            return '';
        return Resources.getResource(key);
    }
    getGroupForItem(type) {
        switch (type) {
            case MarginInfoItemType.KEY_BALANCE:
            case MarginInfoItemType.KEY_AVAILABLE_FUNDS:
            case MarginInfoItemType.KEY_INCOMING_FUNDS:
            case MarginInfoItemType.KEY_MARGIN_AVAILABLE:
            case MarginInfoItemType.KEY_IMPACT_ON_FUNDS:
            case MarginInfoItemType.KEY_IMPACT_ON_MARGIN:
            case MarginInfoItemType.KEY_AFTER_TRADE_FUNDS:
            case MarginInfoItemType.KEY_AFTER_TRADE_MARGIN:
            case MarginInfoItemType.KEY_BLOCKED_FOR_STOCKS:
            case MarginInfoItemType.KEY_SPREAD_INIT_LOSS:
            case MarginInfoItemType.KEY_PL_PER_TICK:
            case MarginInfoItemType.KEY_ALLOW_SHORT_POSITIONS:
            case MarginInfoItemType.KEY_EST_AVAILABLE_CASH:
            case MarginInfoItemType.BLOCKED_FOR_FIXED_INCOME:
                return MarginInfoGroupType.KEY_GROUP_RISKS;
            case MarginInfoItemType.KEY_FILL_PER_LOT:
            case MarginInfoItemType.KEY_ORDER_PER_LOT:
            case MarginInfoItemType.KEY_ORDER_PER_LOT_WITH_FIXED_PART:
            case MarginInfoItemType.KEY_FILL_VOLUME:
            case MarginInfoItemType.KEY_FILL_VOLUME_WITH_MIN_PD:
            case MarginInfoItemType.KEY_PER_FILL:
            case MarginInfoItemType.KEY_PER_TRANSACTION:
            case MarginInfoItemType.KEY_PER_PHONE_TRANSACTION:
            case MarginInfoItemType.KEY_VAT:
            case MarginInfoItemType.KEY_ORDER_VOLUME:
            case MarginInfoItemType.KEY_SHORT_SWAP_INFO:
            case MarginInfoItemType.KEY_FREE_COMMISSION:
            case MarginInfoItemType.KEY_TOTAL_FEES:
            case MarginInfoItemType.KEY_LONG_SWAP:
            case MarginInfoItemType.KEY_SHORT_SWAP:
            case MarginInfoItemType.KEY_ORDER_CARE:
            case MarginInfoItemType.KEY_FILL_FACE_VALUE:
                return MarginInfoGroupType.KEY_GROUP_FEES;
            default: // for custom available funds #106952
                return MarginInfoGroupType.KEY_GROUP_RISKS;
        }
    }
    isColoredItem(type) {
        switch (type) {
            case MarginInfoItemType.KEY_BALANCE:
            case MarginInfoItemType.KEY_AVAILABLE_FUNDS:
            case MarginInfoItemType.KEY_INCOMING_FUNDS:
            case MarginInfoItemType.KEY_MARGIN_AVAILABLE:
            case MarginInfoItemType.KEY_IMPACT_ON_FUNDS:
            case MarginInfoItemType.KEY_IMPACT_ON_MARGIN:
            case MarginInfoItemType.KEY_AFTER_TRADE_FUNDS:
            case MarginInfoItemType.KEY_AFTER_TRADE_MARGIN:
            case MarginInfoItemType.KEY_BLOCKED_FOR_STOCKS:
            case MarginInfoItemType.KEY_SPREAD_INIT_LOSS:
            case MarginInfoItemType.KEY_PL_PER_TICK:
                return true;
            default:
                return false;
        }
    }
    isAccountFeatureType(type) {
        switch (type) {
            case MarginInfoItemType.KEY_BALANCE:
            case MarginInfoItemType.KEY_AVAILABLE_FUNDS:
            case MarginInfoItemType.KEY_INCOMING_FUNDS:
            case MarginInfoItemType.KEY_MARGIN_AVAILABLE:
            case MarginInfoItemType.KEY_BLOCKED_FOR_STOCKS:
            case MarginInfoItemType.KEY_IMPACT_ON_FUNDS:
                return true;
            default:
                return false;
        }
    }
    formatCalculableNumeric(value, format) {
        if (value === undefined) {
            return Resources.getResource('general.Calculating');
        }
        if (!isValidNumber(value)) {
            return null;
        }
        return format(value);
    }
}
export const MarginInfoUtils = new _MarginInfoUtils();
